/**
 * 基于 uid 的版本更新已读状态管理
 *
 * 使用项目统一的 storage 工具存储
 *   VERSION_READ_STATE → { [uid]: { [versionId]: { updateType, contentHash, readTime } } }
 *   VERSION_NEVER_REMIND → { [uid]: boolean }
 */

import { storage } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';

function hashContent(content) {
  if (!content) return '0';
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash.toString(36);
}

export class VersionReadState {
  constructor(uid) {
    this.uid = uid;
  }

  /**
   * 判断该版本配置是否需要提示用户
   * @param {Object} versionConfig - { id, updateType, updateContent }
   *   updateType: 0=强提示 1=中提示 2=静默
   * @returns {boolean} true=需要提示
   */
  shouldNotify(versionConfig) {
    if (!versionConfig?.id) return false;

    const readState = this._getReadState();
    const record = readState[versionConfig.id];

    if (!record) return true;

    if (record.updateType !== versionConfig.updateType) {
      if (record.updateType === 2 && versionConfig.updateType === 2) {
        return false;
      }
      return true;
    }

    if (versionConfig.updateType === 2) {
      return false;
    }

    const newHash = hashContent(versionConfig.updateContent);
    if (record.contentHash !== newHash) {
      return true;
    }

    return false;
  }

  /**
   * 标记版本为已读
   */
  markAsRead(versionConfig) {
    if (!versionConfig?.id) return;

    this._setReadState({
      [versionConfig.id]: {
        updateType: versionConfig.updateType,
        contentHash: hashContent(versionConfig.updateContent),
        readTime: Date.now()
      }
    });
  }

  /**
   * 设置叉号"不再提醒"
   */
  setNeverRemind(value) {
    const allData = storage.get(LOCAL_STORAGE_KEY.VERSION_NEVER_REMIND) || {};
    allData[this.uid] = !!value;
    storage.set(LOCAL_STORAGE_KEY.VERSION_NEVER_REMIND, allData);
  }

  /**
   * 是否勾选了"不再提醒"
   */
  isNeverRemind() {
    const allData = storage.get(LOCAL_STORAGE_KEY.VERSION_NEVER_REMIND) || {};
    return !!allData[this.uid];
  }

  _getReadState() {
    const allData = storage.get(LOCAL_STORAGE_KEY.VERSION_READ_STATE) || {};
    return allData[this.uid] || {};
  }

  _setReadState(state) {
    const allData = storage.get(LOCAL_STORAGE_KEY.VERSION_READ_STATE) || {};
    allData[this.uid] = state;
    storage.set(LOCAL_STORAGE_KEY.VERSION_READ_STATE, allData);
  }
}
